import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useDraggable, useDndMonitor } from '@dnd-kit/core';
import { CircleDot, FolderOpen, X } from 'lucide-react';
import type { TimelineBlock } from '../../../types';
import { useAppStore } from '../../../store';
import { TIMELINE_CONTENT_RIGHT_PAD, TIMELINE_GUTTER_WIDTH, getTimelineBlockVisualKind, timeToMinutes, DEFAULT_DURATION, minutesToTime, SNAP_INTERVAL } from '../../../lib/timeline';
import { deleteTimelineBlock, updateTimelineBlock } from '../../../hooks/useTimelineBlocks';
import { ResizeHandle } from './ResizeHandle';
import type { ColumnLayout } from '../../../lib/timeline';

const priorityColors = {
  p1: { bg: 'bg-p1/12' },
  p2: { bg: 'bg-p2/12' },
  p3: { bg: 'bg-p3/12' },
};

interface DraggableTimelineBlockProps {
  block: TimelineBlock;
  toPixel: (minutes: number) => number;
  durationToPx: (duration: number) => number;
  pxPerHour: number;
  columnLayout?: ColumnLayout;
  startHour: number;
  endHour: number;
}

export function DraggableTimelineBlock({ block, toPixel, durationToPx, pxPerHour, columnLayout, startHour, endHour }: DraggableTimelineBlockProps) {
  const [resizingDuration, setResizingDuration] = useState<number | null>(null);
  const [localStartMin, setLocalStartMin] = useState<number | null>(null);
  const localStartMinRef = useRef<number | null>(null);
  const wheelDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didDrag = useRef(false);

  // Flicker prevention state — populated after useDraggable so isDragging is in scope
  const [pendingTop, setPendingTop] = useState<number | null>(null);
  const lastDragTransformYRef = useRef<number | null>(null);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `timeline-${block.id}`,
    data: {
      type: 'timeline-block',
      blockId: block.id,
      taskId: block.taskId,
      spaceId: block.spaceId,
      scheduledStartTime: block.scheduledStartTime,
      scheduledDuration: block.scheduledDuration ?? DEFAULT_DURATION,
    },
  });

  const startMin = timeToMinutes(block.scheduledStartTime);
  const duration = resizingDuration ?? (block.scheduledDuration ?? DEFAULT_DURATION);
  const effectiveStartMin = localStartMin ?? startMin;
  const top = toPixel(effectiveStartMin);
  const height = Math.max(durationToPx(duration), 24);
  const endMin = effectiveStartMin + duration;
  const colors = priorityColors[block.priority as keyof typeof priorityColors] ?? priorityColors.p2;
  const visualKind = getTimelineBlockVisualKind(block);
  const isShort = height < 36;
  const isResizing = resizingDuration !== null;

  if (isDragging) didDrag.current = true;

  // Capture y-delta while dragging so useLayoutEffect can hold the drop position
  if (isDragging && transform) lastDragTransformYRef.current = transform.y;

  // On drag end: lock block at visual drop position before browser paints
  useLayoutEffect(() => {
    if (!isDragging && lastDragTransformYRef.current !== null) {
      setPendingTop(top + lastDragTransformYRef.current);
      lastDragTransformYRef.current = null;
    }
  }, [isDragging, top]);

  useDndMonitor({
    onDragCancel({ active }) {
      if (active.id === `timeline-${block.id}`) {
        lastDragTransformYRef.current = null; // prevent layout effect from setting pendingTop
        setPendingTop(null);                  // clear if already set
      }
    },
  });

  // Clear pendingTop once cache-driven top catches up to where we dropped
  useEffect(() => {
    if (pendingTop !== null && Math.abs(top - pendingTop) < 2) {
      setPendingTop(null);
    }
  }, [top, pendingTop]);

  // Safety net: clear pendingTop if it hasn't resolved within 700ms.
  // Covers: dropped outside any drop zone (no mutation fires),
  // and API error rollback (cache reverts, top never matches pendingTop).
  useEffect(() => {
    if (pendingTop === null) return;
    const t = setTimeout(() => setPendingTop(null), 700);
    return () => clearTimeout(t);
  }, [pendingTop]);

  // Clear local optimistic state once the server value catches up
  useEffect(() => {
    if (localStartMin !== null && startMin === localStartMin) {
      setLocalStartMin(null);
      localStartMinRef.current = null;
    }
  }, [startMin, localStartMin]);

  useEffect(() => {
    const flush = () => {
      if (wheelDebounceRef.current && localStartMinRef.current !== null) {
        clearTimeout(wheelDebounceRef.current);
        updateTimelineBlock(block.id, block.date, { scheduledStartTime: minutesToTime(localStartMinRef.current) });
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => {
      flush();
      window.removeEventListener('beforeunload', flush);
    };
  }, [block.id, block.date]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const direction = e.deltaY > 0 ? 1 : -1;
    const current = localStartMinRef.current ?? startMin;
    const newMin = current + direction * SNAP_INTERVAL;
    const minBound = startHour * 60;
    const maxBound = endHour * 60 - duration;
    if (newMin < minBound || newMin > maxBound) return;

    // Update local state immediately for smooth visual feedback
    localStartMinRef.current = newMin;
    setLocalStartMin(newMin);

    // Debounce: only fire the API call once the scroll gesture stops
    if (wheelDebounceRef.current) clearTimeout(wheelDebounceRef.current);
    wheelDebounceRef.current = setTimeout(() => {
      updateTimelineBlock(block.id, block.date, { scheduledStartTime: minutesToTime(localStartMinRef.current!) });
    }, 300);
  }, [block.id, block.date, startMin, startHour, endHour, duration]);

  const handleClick = () => {
    if (didDrag.current) {
      didDrag.current = false;
      return;
    }
    useAppStore.getState().setHighlightedTaskId(block.taskId);
    setTimeout(() => {
      useAppStore.getState().setHighlightedTaskId(null);
    }, 1500);
  };

  const col = columnLayout?.column ?? 0;
  const totalCols = columnLayout?.totalColumns ?? 1;

  const style: React.CSSProperties = {
    top: pendingTop ?? top,
    height,
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging || isResizing ? 50 : 10,
    cursor: isDragging ? 'grabbing' : 'grab',
    ...(totalCols > 1 ? {
      left: `calc(${TIMELINE_GUTTER_WIDTH}px + ${(col / totalCols) * 100}% - ${(col / totalCols) * TIMELINE_GUTTER_WIDTH}px)`,
      width: `calc(${100 / totalCols}% - ${TIMELINE_GUTTER_WIDTH / totalCols}px - ${TIMELINE_CONTENT_RIGHT_PAD / totalCols}px)`,
    } : {
      left: TIMELINE_GUTTER_WIDTH,
      right: TIMELINE_CONTENT_RIGHT_PAD,
    }),
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      onWheel={handleWheel}
      className={`absolute rounded-md overflow-visible select-none transition-shadow group
        ${colors.bg}
        ${isDragging ? 'shadow-lg shadow-accent/20 ring-1 ring-accent/30' : 'hover:shadow-sm'}
        ${isResizing ? 'ring-1 ring-accent/30' : ''}`}
      style={style}
      title={`${block.title} (${minutesToTime(effectiveStartMin)} – ${minutesToTime(endMin)})`}
    >
      <button
        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-70 bg-bg-surface/80 hover:bg-p1/20 text-text-muted hover:text-p1 transition-opacity z-10"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); deleteTimelineBlock(block.id, block.date); }}
        title="Remove from timeline"
      >
        <X size={10} />
      </button>
      <div className={`px-2 ${isShort ? 'py-0.5 flex items-center gap-2' : 'py-1.5'}`}>
        <div className="flex items-center gap-1.5 min-w-0">
          {!block.parentId && block.itemType === 'project' && (
            <FolderOpen size={10} className="shrink-0" style={{ color: block.color ?? '#6366f1' }} />
          )}
          {!block.parentId && block.itemType !== 'project' && (
            <CircleDot size={10} className="shrink-0 text-accent" />
          )}
          <span className="text-micro font-medium text-text-primary truncate block">
            {block.title}
          </span>
        </div>
        {!isShort && (
          <span className="text-micro text-text-muted font-mono">
            {minutesToTime(effectiveStartMin)} – {minutesToTime(endMin)}
          </span>
        )}
      </div>
      <ResizeHandle
        block={block}
        currentDuration={block.scheduledDuration ?? DEFAULT_DURATION}
        pxPerHour={pxPerHour}
        onResizing={setResizingDuration}
      />
    </div>
  );
}
